import { UseNavigatorResult } from "@saleor/hooks/useNavigator";
import { PageInfo, PaginationState } from "@saleor/hooks/usePaginator";
import { stringifyQs } from "@saleor/utils/urls";

export interface ProductListProps {
  productPageInfo?: {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  onNextProductPage: () => void;
  onPreviousProductPage: () => void;
}

export type ProductPagination = Partial<{
  afterproduct: string;
  beforeproduct: string;
}>;

export function createProductPaginationState(
  paginateBy: number,
  queryString: ProductPagination
): PaginationState {
  return queryString && (queryString.beforeproduct || queryString.afterproduct)
    ? queryString.afterproduct
      ? {
          after: queryString.afterproduct,
          first: paginateBy
        }
      : {
          before: queryString.beforeproduct,
          last: paginateBy
        }
    : {
        first: paginateBy
      };
}

export function productPaginate(
  pageInfo: PageInfo,
  paginationState: PaginationState,
  queryString: ProductPagination,
  navigate: UseNavigatorResult
) {
  const loadNextProductPage = () =>
    navigate(
      "?" +
        stringifyQs({
          ...queryString,
          afterproduct: pageInfo.endCursor,
          beforeproduct: undefined
        }),
      true
    );

  const loadPreviousProductPage = () =>
    navigate(
      "?" +
        stringifyQs({
          ...queryString,
          afterproduct: undefined,
          beforeproduct: pageInfo.startCursor
        }),
      true
    );

  const newPageInfo = pageInfo
    ? {
        ...pageInfo,
        hasNextPage: !!paginationState.before || pageInfo.hasNextPage,
        hasPreviousPage: !!paginationState.after || pageInfo.hasPreviousPage
      }
    : undefined;

  return {
    loadNextProductPage,
    loadPreviousProductPage,
    pageProductInfo: newPageInfo
  };
}

export interface CustomerListProps {
  customerPageInfo?: {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  onNextCustomerPage: () => void;
  onPreviousCustomerPage: () => void;
}

export type CustomerPagination = Partial<{
  aftercustomer: string;
  beforecustomer: string;
}>;

export function createCustomerPaginationState(
  paginateBy: number,
  queryString: CustomerPagination
): PaginationState {
  return queryString &&
    (queryString.beforecustomer || queryString.aftercustomer)
    ? queryString.aftercustomer
      ? {
          after: queryString.aftercustomer,
          first: paginateBy
        }
      : {
          before: queryString.beforecustomer,
          last: paginateBy
        }
    : {
        first: paginateBy
      };
}

export function customerPaginate(
  pageInfo: PageInfo,
  paginationState: PaginationState,
  queryString: CustomerPagination,
  navigate: UseNavigatorResult
) {
  const loadNextCustomerPage = () =>
    navigate(
      "?" +
        stringifyQs({
          ...queryString,
          aftercustomer: pageInfo.endCursor,
          beforecustomer: undefined
        }),
      true
    );

  const loadPreviousCustomerPage = () =>
    navigate(
      "?" +
        stringifyQs({
          ...queryString,
          aftercustomer: undefined,
          beforecustomer: pageInfo.startCursor
        }),
      true
    );

  const newPageInfo = pageInfo
    ? {
        ...pageInfo,
        hasNextPage: !!paginationState.before || pageInfo.hasNextPage,
        hasPreviousPage: !!paginationState.after || pageInfo.hasPreviousPage
      }
    : undefined;

  return {
    loadNextCustomerPage,
    loadPreviousCustomerPage,
    pageCustomerInfo: newPageInfo
  };
}

export interface MallsListProps {
  mallsPageInfo?: {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  onNextMallsPage: () => void;
  onPreviousMallsPage: () => void;
}

export type MallsPagination = Partial<{
  aftermalls: string;
  beforemalls: string;
}>;

export function createMallsPaginationState(
  paginateBy: number,
  queryString: MallsPagination
): PaginationState {
  return queryString && (queryString.beforemalls || queryString.aftermalls)
    ? queryString.aftermalls
      ? {
          after: queryString.aftermalls,
          first: paginateBy
        }
      : {
          before: queryString.beforemalls,
          last: paginateBy
        }
    : {
        first: paginateBy
      };
}

export function mallsPaginate(
  pageInfo: PageInfo,
  paginationState: PaginationState,
  queryString: MallsPagination,
  navigate: UseNavigatorResult
) {
  const loadNextMallsPage = () =>
    navigate(
      "?" +
        stringifyQs({
          ...queryString,
          aftermalls: pageInfo.endCursor,
          beforemalls: undefined
        }),
      true
    );

  const loadPreviousMallsPage = () =>
    navigate(
      "?" +
        stringifyQs({
          ...queryString,
          aftermalls: undefined,
          beforemalls: pageInfo.startCursor
        }),
      true
    );

  const newPageInfo = pageInfo
    ? {
        ...pageInfo,
        hasNextPage: !!paginationState.before || pageInfo.hasNextPage,
        hasPreviousPage: !!paginationState.after || pageInfo.hasPreviousPage
      }
    : undefined;

  return {
    loadNextMallsPage,
    loadPreviousMallsPage,
    pageMallsInfo: newPageInfo
  };
}
